import { useState, useEffect, useRef } from 'react'

import { motion, AnimatePresence } from 'framer-motion'
import { invalidate } from '@react-three/fiber'
import { AiFillCamera, AiOutlineArrowLeft, AiOutlineHighlight, AiFillMail, AiOutlineFilePdf } from 'react-icons/ai'
import { useSnapshot } from 'valtio'
import { state } from './store'

// Helper function to clean material name for display (remove underscore and everything after)
const getDisplayName = (materialName) => {
  return materialName.split('_')[0]
}

// Helper function to generate PDF with screenshot and order details
const generatePDF = (snap, modelMaterials, getDisplayName) => {
  // Reset camera to original position
  const controls = state.orbitControlsRef;
  if (controls && state.defaultCameraPosition) {
    controls.reset();
  }

  // Wait for camera to settle before taking first screenshot
  setTimeout(() => {
    // Detect if mobile for different zoom behavior
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 600;
    
    // On mobile: zoom out slightly (1.15 = 15% further), on desktop: zoom in (0.65 = 35% closer)
    if (controls) {
      const currentDistance = controls.getDistance();
      const zoomFactor = isMobile ? 1.15 : 0.65;
      const zoomedDistance = currentDistance * zoomFactor;
      controls.object.position.normalize().multiplyScalar(zoomedDistance);
      controls.object.position.add(controls.target);
      
      // Rotate camera to the left for first screenshot (add π/4 radians = 45 degrees)
      const currentAzimuth = controls.getAzimuthalAngle();
      controls.setAzimuthalAngle(currentAzimuth - Math.PI / 5.7);
      
      controls.update();
    }

    // Wait a bit for zoom to settle
    setTimeout(() => {
      // Get canvas for first screenshot
      const canvas = document.querySelector('canvas');
      if (!canvas) {
        alert('Canvas not found. Please try again.');
        return;
      }

      const scaleFactor = 0.6;
      const scaledCanvas = document.createElement('canvas');
      scaledCanvas.width = Math.max(1, Math.floor(canvas.width * scaleFactor));
      scaledCanvas.height = Math.max(1, Math.floor(canvas.height * scaleFactor));
      const scaledCtx = scaledCanvas.getContext('2d');
      if (!scaledCtx) {
        alert('Unable to prepare image. Please try again.');
        return;
      }
      scaledCtx.imageSmoothingEnabled = true;
      scaledCtx.imageSmoothingQuality = 'high';
      scaledCtx.drawImage(canvas, 0, 0, scaledCanvas.width, scaledCanvas.height);

      const imgData1 = scaledCanvas.toDataURL('image/png');

      // Reset camera back to original position
      if (controls) {
        controls.reset();
      }

      // Dynamically import jsPDF
      import('jspdf').then((jsPDFModule) => {
        const jsPDF = jsPDFModule.default;

        // Create new PDF documentx
        const doc = new jsPDF();

        // Add title
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.text('DAPAPOD Configuration', 105, 20, { align: 'center' });

        // Add model info
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(`Selected Model: ${snap.selectedModel}`, 20, 35);

        // Calculate dimensions for screenshots
        const canvasAspectRatio = canvas.width / canvas.height;
        let imgWidth, imgHeight;

        // Calculate dimensions based on aspect ratio - larger images stacked vertically
        if (canvasAspectRatio > 1) {
          // Landscape or square - fit to width
          imgWidth = 170;
          imgHeight = imgWidth / canvasAspectRatio;
        } else {
          // Portrait - fit to height with max width
          const maxHeight = 80;
          imgHeight = maxHeight;
          imgWidth = imgHeight * canvasAspectRatio;

          // If calculated width is too wide, scale down
          if (imgWidth > 170) {
            imgWidth = 170;
            imgHeight = imgWidth / canvasAspectRatio;
          }
        }

        // Add screenshot
        doc.addImage(imgData1, 'PNG', 20, 45, imgWidth, imgHeight);

        // Calculate position for text after the image
        const textStartY = 45 + imgHeight + 10;

    // Add material selections
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Material Selections:', 20, textStartY);

    const currentModelMaterials = modelMaterials[snap.selectedModel] || [];
    const currentModelPickedItems = snap.pickedItems.filter(item => item.model === snap.selectedModel);

    let yPosition = textStartY + 10;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');

    currentModelMaterials.forEach((material, index) => {
      if (yPosition > 280) { // Check if we need a new page
        doc.addPage();
        yPosition = 20;
      }

      const pickedItem = currentModelPickedItems.find(item => item.material === material);
      const colorName = pickedItem ? pickedItem.color : 'Default';
      const materialText = `${index + 1}. ${getDisplayName(material)}: ${colorName}`;

      doc.text(materialText, 25, yPosition);
      yPosition += 8;
    });

    // Add addons section
    yPosition += 10;
    if (yPosition > 270) {
      doc.addPage();
      yPosition = 20;
    }

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Add-ons:', 20, yPosition);
    yPosition += 10;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    if (snap.enabledAddons && snap.enabledAddons.length > 0) {
      doc.text(snap.enabledAddons.join(', '), 25, yPosition);
    } else {
      doc.text('None selected', 25, yPosition);
    }

    // Add footer with date
    const currentDate = new Date().toLocaleDateString();
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.text(`Generated on: ${currentDate}`, 20, 285);

        // Save the PDF - use blob method for iframe compatibility
        try {
          const pdfBlob = doc.output('blob');
          const blobUrl = URL.createObjectURL(pdfBlob);
          
          // Create a temporary link element
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = `${snap.selectedModel}_Configuration.pdf`;
          link.style.display = 'none';
          
          // Append to body, click, and remove
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Clean up the blob URL after a short delay
          setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
        } catch (error) {
          console.error('Error saving PDF:', error);
          // Fallback to regular save method
          doc.save(`${snap.selectedModel}_Configuration.pdf`);
        }
      }).catch((error) => {
        console.error('Error loading jsPDF:', error);
        alert('PDF generation failed. Please try again.');
      });
    }, 200); // Wait 200ms for zoom to settle
  }, 300); // Wait 300ms for first camera position to settle
};

// Helper function to update enabled addons list
const updateEnabledAddons = (snap) => {
  const addons = []

  if (snap.frostedGlass) addons.push('Frosted Glass')
  if (snap.showWheels) addons.push('Wheels')
  if (snap.showDoor) addons.push('Door')
  if ((snap.selectedModel === 'QuadPod' || snap.selectedModel === 'DoublePod') && snap.showTV) {
    addons.push('TV')
  }

  state.enabledAddons = addons
}

export function Overlay() {
  const snap = useSnapshot(state)

  // Helper function to determine text color based on selected color
  const getTextColor = () => {
    if (!snap.color) return 'white'
    return snap.color === '#F4F9FC' ? 'black' : 'white'
  }

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 600

  const transition = { type: 'spring', duration: 0.8 }
  const config = {
    initial: { x: -100, opacity: 0, transition: { ...transition, delay: 0.5 } },
    animate: { x: 0, opacity: 1, transition: { ...transition, delay: 0 } },
    exit: { x: -100, opacity: 0, transition: { ...transition, delay: 0 } }
  }
  return (
    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
      <AnimatePresence>
        {snap.intro ? (
          <motion.section key="main" {...config} style={{ backdropFilter: 'blur(8px)' }}>
            <div className="section--container" onClick={() => (state.intro = false)}>
              <motion.div
                key="logo"
                initial={{ x: 100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ type: 'spring', damping: 5, stiffness: 40, restDelta: 0.001, duration: 0.3 }}>
                <img
                  src="/logo.svg"
                  alt="DAPAPOD Logo"
                  style={{
                    maxWidth: isMobile ? '90%' : '100%',
                    // height: 'auto',
                    maxHeight: isMobile ? '120px' : '200px',
                    objectFit: 'contain',
                    marginBottom: isMobile ? '30px' : '40px'
                  }}
                />
              </motion.div>
              <div className="support--content">
                <motion.div
                  key="p"
                  initial={{ y: 100, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{
                    type: 'spring',
                    damping: 7,
                    stiffness: 30,
                    restDelta: 0.001,
                    duration: 0.6,
                    delay: 0.2,
                    delayChildren: 0.2
                  }}>
                  <button style={{ background: snap.color || '#DCA127', color: getTextColor() }} onClick={() => (state.intro = false)}>
                    CUSTOMIZE <AiOutlineHighlight size="1.3em" />
                  </button>
                </motion.div>
              </div>
            </div>
          </motion.section>
        ) : (
          <motion.section key="custom" {...config}>
            <Customizer />
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  )
}

export function Customizer() {
  const snap = useSnapshot(state)
  const [isOpen, setIsOpen] = useState(true)
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false)
  const [hoveredColor, setHoveredColor] = useState(null)
  const [selectedColorName, setSelectedColorName] = useState(null)
  const [isMobilePanelExpanded, setIsMobilePanelExpanded] = useState(false)
  const [mobilePanelHeight, setMobilePanelHeight] = useState(60)
  const [isDraggingPanel, setIsDraggingPanel] = useState(false)

  const panelRef = useRef(null)
  const dragStateRef = useRef(null)

  // Initialize enabled addons on component mount
  useEffect(() => {
    updateEnabledAddons(snap)
  }, [snap.showDoor, snap.showWheels, snap.showTV, snap.frostedGlass, snap.selectedModel])

  // Stop propagation of wheel events to prevent zooming
  useEffect(() => {
    const panel = panelRef.current
    if (panel) {
      const stopScroll = (e) => e.stopPropagation()
      panel.addEventListener('wheel', stopScroll, { passive: false })
      return () => panel.removeEventListener('wheel', stopScroll)
    }
  }, [isEditPanelOpen])

  // Ensure selected material is valid for the current model
  useEffect(() => {
    const currentModelMaterials = modelMaterials[snap.selectedModel] || []
    if (currentModelMaterials.length > 0 && !currentModelMaterials.includes(snap.selectedMaterial)) {
      // If current selected material doesn't exist in the new model, select the first one
      state.selectedMaterial = currentModelMaterials[0]
    }
  }, [snap.selectedModel])

  // Helper function to determine text color based on selected color
  const getTextColor = () => {
    if (!snap.color) return 'white'
    return snap.color === '#F4F9FC' ? 'grey' : 'white'
  }

  // Define material lists per model
  const modelMaterials = {
    StandingPod: ['Shell Exterior Colour_STP', 'Internal Facing Wall_STP', 'Internal Ceiling Colour_STP', 'Internal Rear Wall Colour_STP', 'Exterior Backwall Colour_STP', 'Poly laminate Face_STP', 'Poly laminate Door_STP'],
    SinglePod: ['Shell Exterior Colour_SP', 'Internal Facing Wall_SP', 'Internal Seating Colour_SP', 'Internal Ceiling Colour_SP', 'Front Trim Colour_SP', 'Internal Rear Wall Colour_SP', 'Exterior Backwall Colour_SP', 'Poly laminate Face_SP', 'Poly laminate Door_SP'],
    DoublePod: ['Shell Exterior Colour_DP', 'Internal Seating Colour_DP', 'Internal Ceiling Colour_DP', 'Front Trim Colour_DP', 'Internal Rear Wall Colour_DP', 'Exterior Backwall Colour_DP', 'Poly laminate Face_DP', 'Poly laminate Door_DP'],
    QuadPod: ['Shell Exterior Colour_QP', 'Internal Seating Colour_QP', 'Internal Ceiling Colour_QP', 'Front Trim Colour_QP', 'Internal Rear Wall Colour_QP', 'Exterior Backwall Colour_QP', 'Poly laminate Face_QP', 'Poly laminate Door_QP'],
  }

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 600

  const getMobilePanelMaxHeight = () => {
    if (typeof window === 'undefined') return 360
    return Math.round(window.innerHeight * 0.6)
  }

  useEffect(() => {
    if (!isMobile) return
    const maxHeight = getMobilePanelMaxHeight()
    setMobilePanelHeight(isMobilePanelExpanded ? maxHeight : 60)
  }, [isMobilePanelExpanded, isMobile])

  const getClientY = (event) => {
    if (event.touches && event.touches[0]) return event.touches[0].clientY
    if (event.changedTouches && event.changedTouches[0]) return event.changedTouches[0].clientY
    return event.clientY
  }

  const handleMobilePanelDragStart = (event) => {
    if (!isMobile) return

    const controls = state.orbitControlsRef
    if (controls) {
      controls.enabled = false
    }
    state.isDraggingPanel = true

    const startY = getClientY(event)
    const startHeight = mobilePanelHeight
    const maxHeight = getMobilePanelMaxHeight()
    const dragThreshold = 5 // pixels before considering it a drag

    dragStateRef.current = { 
      startY, 
      startHeight, 
      maxHeight, 
      currentHeight: startHeight,
      isDragging: false,
      hasMoved: false
    }

    const handleMove = (moveEvent) => {
      if (!dragStateRef.current) return
      
      const currentY = getClientY(moveEvent)
      const distance = Math.abs(dragStateRef.current.startY - currentY)
      
      // Only start dragging if moved beyond threshold
      if (!dragStateRef.current.isDragging && distance > dragThreshold) {
        dragStateRef.current.isDragging = true
      }

      if (dragStateRef.current.isDragging) {
        moveEvent.preventDefault()
        moveEvent.stopPropagation()
        dragStateRef.current.hasMoved = true
        setIsDraggingPanel(true)
        state.isDraggingPanel = true
        
        const delta = dragStateRef.current.startY - currentY
        const nextHeight = Math.min(
          dragStateRef.current.maxHeight,
          Math.max(60, dragStateRef.current.startHeight + delta)
        )
        dragStateRef.current.currentHeight = nextHeight
        setMobilePanelHeight(nextHeight)
      }
    }

    const handleEnd = (endEvent) => {
      if (!dragStateRef.current) return
      
      const { maxHeight, currentHeight, hasMoved } = dragStateRef.current
      
      if (hasMoved) {
        // It was a drag - set height based on position
        endEvent.preventDefault()
        endEvent.stopPropagation()
        const shouldExpand = currentHeight > (60 + maxHeight) / 2
        setIsMobilePanelExpanded(shouldExpand)
        setMobilePanelHeight(shouldExpand ? maxHeight : 60)
      } else {
        // It was a click/tap - toggle state
        setIsMobilePanelExpanded(!isMobilePanelExpanded)
      }
      
      dragStateRef.current = null
      setIsDraggingPanel(false)
      state.isDraggingPanel = false
      if (controls) {
        controls.enabled = true
      }
      window.removeEventListener('mousemove', handleMove)
      window.removeEventListener('mouseup', handleEnd)
      window.removeEventListener('touchmove', handleMove)
      window.removeEventListener('touchend', handleEnd)
      window.removeEventListener('touchcancel', handleEnd)
    }

    window.addEventListener('mousemove', handleMove)
    window.addEventListener('mouseup', handleEnd)
    window.addEventListener('touchmove', handleMove, { passive: false })
    window.addEventListener('touchend', handleEnd)
    window.addEventListener('touchcancel', handleEnd)
  }

  return (
    <div className="customizer" style={{ position: 'relative' }}>
      {/* EDIT button - hide on mobile, use bottom panel instead */}
      {!isMobile && (
        <button
          onClick={() => setIsEditPanelOpen(!isEditPanelOpen)}
          style={{
            position: 'absolute',
            top: '22%',
            right: isEditPanelOpen ? '320px' : '40px',
            transform: 'translateY(-50%)',
            zIndex: 10,
            background: snap.color || '#DCA127',
            borderRadius: '8px',
            padding: '10px 16px',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            color: getTextColor(),
            fontSize: '0.9rem',
            fontWeight: '500',
            minWidth: '70px',
            transition: 'all 0.3s ease',
          }}
        >
          EDIT {isEditPanelOpen ? '▶' : '◀'}
        </button>
      )}

      {/* Expandable Edit Panel - Desktop version */}
      <AnimatePresence>
        {isEditPanelOpen && !isMobile && (
          <motion.div
            ref={panelRef}
            initial={{ opacity: 0, x: 350 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 350 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            style={{
              position: 'absolute',
              top: '0',
              right: '10px',
              height: '100vh',
              width: '300px',
              zIndex: 9,
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '12px',
              padding: '24px 20px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              backdropFilter: 'blur(4px)',
              overflow: 'auto',
            }}
          >

            {/* Material Picker Section */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <h3 style={{ margin: '0', color: '#333', fontSize: '1.1rem', fontWeight: '600' }}>Material Selection</h3>
              <select
                value={snap.selectedMaterial}
                onChange={(e) => (state.selectedMaterial = e.target.value)}
                style={{
                  width: '100%',
                  fontSize: '0.9rem',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #ddd',
                  backgroundColor: 'white',
                  color: '#333',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                  fontWeight: '500',
                }}
              >
                {(modelMaterials[snap.selectedModel] || []).map((mat) => (
                  <option
                    key={mat}
                    value={mat}
                    style={{
                      backgroundColor: 'white',
                      color: '#333',
                    }}
                  >
                    {getDisplayName(mat)}
                  </option>
                ))}
              </select>
            </div>

            {/* Separator */}
            <hr style={{ border: 'none', borderTop: '1px solid #ddd', margin: '10px 0' }} />

            {/* Color Picker Section */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ margin: '0', color: '#333', fontSize: '1.1rem', fontWeight: '600' }}>Colors</h3>
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  style={{
                    background: snap.color || '#DCA127',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '6px 10px',
                    color: getTextColor(),
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    fontWeight: '500',
                  }}
                >
                  {isOpen ? 'Hide' : 'Show'} <AiOutlineHighlight size="1em" />
                </button>
              </div>

              {/* Color options grid */}
              <AnimatePresence>
                {isOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(24px, 1fr))',
                      gap: '8px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      padding: '10px',
                      background: 'rgba(0,0,0,0.05)',
                      borderRadius: '8px',
                    }}
                  >
                    {snap.colors.map((color) => (
                      <div
                        key={color.colorHex}
                        style={{
                          background: color.colorHex,
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          cursor: 'pointer',
                          border: '2px solid white',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          position: 'relative',
                          transition: 'transform 0.2s ease',
                          transform: hoveredColor === color.name ? 'scale(1.1)' : 'scale(1)',
                        }}
                        onMouseEnter={() => setHoveredColor(color.name)}
                        onMouseLeave={() => setHoveredColor(null)}
                        onClick={() => {
                          state.color = color.colorHex
                          setSelectedColorName(color.name)

                          // Update pickedItems - keep only latest color per material per model
                          const existingItemIndex = state.pickedItems.findIndex(
                            item => item.material === snap.selectedMaterial && item.model === snap.selectedModel
                          )

                          if (existingItemIndex >= 0) {
                            // Update existing item
                            state.pickedItems[existingItemIndex] = {
                              model: snap.selectedModel,
                              material: snap.selectedMaterial,
                              color: color.name,
                              colorHex: color.colorHex
                            }
                          } else {
                            // Add new item
                            state.pickedItems.push({
                              model: snap.selectedModel,
                              material: snap.selectedMaterial,
                              color: color.name,
                              colorHex: color.colorHex
                            })
                          }
                          invalidate()
                        }}
                        title={color.name}
                      />
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Default Button */}
              {isOpen && (
                <button
                  onClick={() => {
                    // Check if we have original material stored
                    const hasOriginalMaterial = Object.keys(snap.originalMaterials).some(materialName =>
                      materialName.toLowerCase().includes(snap.selectedMaterial.toLowerCase())
                    )

                    if (hasOriginalMaterial) {
                      // Set the display to show it's using original material
                      setSelectedColorName('Original Material')

                      // Update pickedItems to mark this material as using original
                      const existingItemIndex = state.pickedItems.findIndex(
                        item => item.material === snap.selectedMaterial && item.model === snap.selectedModel
                      )

                      if (existingItemIndex >= 0) {
                        // Update existing item to original
                        state.pickedItems[existingItemIndex] = {
                          model: snap.selectedModel,
                          material: snap.selectedMaterial,
                          color: 'Original Material'
                        }
                      } else {
                        // Add new item with original material marker
                        state.pickedItems.push({
                          model: snap.selectedModel,
                          material: snap.selectedMaterial,
                          color: 'Original Material'
                        })
                      }
                      invalidate()
                    } else {
                      // Fallback: just remove from pickedItems if no original material stored
                      state.pickedItems = state.pickedItems.filter(
                        item => !(item.material === snap.selectedMaterial && item.model === snap.selectedModel)
                      )
                      setSelectedColorName(null)
                      invalidate()
                    }
                  }}
                  style={{
                    backgroundColor: 'grey',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    marginTop: '8px',
                    width: '100%',
                  }}
                >
                  RESET TO DEFAULT
                </button>
              )}
            </div>

            {/* Selected Color Display */}
            {(hoveredColor || selectedColorName) && (
              <div style={{
                padding: '8px 12px',
                backgroundColor: '#f8f9fa',
                borderRadius: '6px',
                marginTop: '10px',
                border: '1px solid #e9ecef',
              }}>
                <div style={{
                  fontSize: '0.9rem',
                  color: '#495057',
                  fontWeight: '500',
                }}>
                  {hoveredColor ? `${hoveredColor}` : `${selectedColorName}`}
                </div>
              </div>
            )}

            {/* Separator */}
            <hr style={{ border: 'none', borderTop: '1px solid #ddd', margin: '10px 0' }} />

            {/* Add-Ons Section */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#333', fontSize: '1.1rem', fontWeight: '600' }}>Add-Ons</h3>

              {/* Door checkbox */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  onClick={() => {
                    state.showDoor = !snap.showDoor
                    updateEnabledAddons({ ...snap, showDoor: !snap.showDoor })
                  }}
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '4px',
                    border: `2px solid ${snap.showDoor ? '#000000' : '#ddd'}`,
                    backgroundColor: snap.showDoor ? '#000000' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: snap.showDoor ? '0 0 0 3px rgba(0,0,0,0.1)' : 'none',
                  }}
                >
                  {snap.showDoor && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20,6 9,17 4,12"></polyline>
                    </svg>
                  )}
                </div>
                <label
                  onClick={() => {
                    state.showDoor = !snap.showDoor
                    updateEnabledAddons({ ...snap, showDoor: !snap.showDoor })
                  }}
                  style={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    fontWeight: '500',
                    color: '#333',
                    fontSize: '0.95rem'
                  }}
                >
                  Show Door
                </label>
              </div>

              {/* Wheels checkbox */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  onClick={() => {
                    state.showWheels = !snap.showWheels
                    updateEnabledAddons({ ...snap, showWheels: !snap.showWheels })
                  }}
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '4px',
                    border: `2px solid ${snap.showWheels ? '#000000' : '#ddd'}`,
                    backgroundColor: snap.showWheels ? '#000000' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: snap.showWheels ? '0 0 0 3px rgba(0,0,0,0.1)' : 'none',
                  }}
                >
                  {snap.showWheels && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20,6 9,17 4,12"></polyline>
                    </svg>
                  )}
                </div>
                <label
                  onClick={() => {
                    state.showWheels = !snap.showWheels
                    updateEnabledAddons({ ...snap, showWheels: !snap.showWheels })
                  }}
                  style={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    fontWeight: '500',
                    color: '#333',
                    fontSize: '0.95rem'
                  }}
                >
                  Show Wheels
                </label>
              </div>

              {/* TV checkbox - show for multi-person pods */}
              {(snap.selectedModel === 'QuadPod' || snap.selectedModel === 'DoublePod') && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div
                    onClick={() => {
                      state.showTV = !snap.showTV
                      updateEnabledAddons({ ...snap, showTV: !snap.showTV })
                    }}
                    style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '4px',
                      border: `2px solid ${snap.showTV ? '#000000' : '#ddd'}`,
                      backgroundColor: snap.showTV ? '#000000' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: snap.showTV ? '0 0 0 3px rgba(0,0,0,0.1)' : 'none',
                    }}
                  >
                    {snap.showTV && (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20,6 9,17 4,12"></polyline>
                      </svg>
                    )}
                  </div>
                  <label
                    onClick={() => {
                      state.showTV = !snap.showTV
                      updateEnabledAddons({ ...snap, showTV: !snap.showTV })
                    }}
                    style={{
                      cursor: 'pointer',
                      userSelect: 'none',
                      fontWeight: '500',
                      color: '#333',
                      fontSize: '0.95rem'
                    }}
                  >
                    Show TV
                  </label>
                </div>
              )}

              {/* Frosted Glass checkbox */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  onClick={() => {
                    state.frostedGlass = !snap.frostedGlass
                    updateEnabledAddons({ ...snap, frostedGlass: !snap.frostedGlass })
                  }}
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '4px',
                    border: `2px solid ${snap.frostedGlass ? '#000000' : '#ddd'}`,
                    backgroundColor: snap.frostedGlass ? '#000000' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: snap.frostedGlass ? '0 0 0 3px rgba(0,0,0,0.1)' : 'none',
                  }}
                >
                  {snap.frostedGlass && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20,6 9,17 4,12"></polyline>
                    </svg>
                  )}
                </div>
                <label
                  onClick={() => {
                    state.frostedGlass = !snap.frostedGlass
                    updateEnabledAddons({ ...snap, frostedGlass: !snap.frostedGlass })
                  }}
                  style={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    fontWeight: '500',
                    color: '#333',
                    fontSize: '0.95rem'
                  }}
                >
                  Frosted Glass
                </label>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: 'auto' }}>
              <button
                style={{
                  background: snap.color || '#DCA127',
                  color: getTextColor(),
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  fontWeight: '500',
                  fontSize: '0.9rem',
                }}
                onClick={() => {
                  generatePDF(snap, modelMaterials, getDisplayName)
                }}
              >
                PDF
                <AiOutlineFilePdf size="1.3em" />
              </button>

              <button
                style={{
                  background: snap.color || '#DCA127',
                  color: getTextColor(),
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  fontWeight: '500',
                  fontSize: '0.9rem',
                }}
                onClick={() => {
                  // Get all materials for the current model
                  const currentModelMaterials = modelMaterials[snap.selectedModel] || []

                  // Get picked items for current model only
                  const currentModelPickedItems = snap.pickedItems.filter(item => item.model === snap.selectedModel)

                  // Build complete material list with colors or "Default"
                  const materialList = currentModelMaterials.map((material, index) => {
                    const pickedItem = currentModelPickedItems.find(item => item.material === material)
                    const colorName = pickedItem ? pickedItem.color : 'Default'
                    return `${index + 1}. Material: ${getDisplayName(material)}, Color: ${colorName}`
                  }).join('\n')

                  // Build addons list
                  const addonsList = snap.enabledAddons.length > 0
                    ? `Add-ons: ${snap.enabledAddons.join(', ')}`
                    : 'Add-ons: None selected'

                  const bodyText = `Order Details:
Selected Model: ${snap.selectedModel}

Material Selections:
${materialList}

${addonsList}
`

                  // Encode for URL usage
                  const encodedBody = encodeURIComponent(bodyText)

                  // Construct mailto link
                  const mailtoLink = `mailto:info@dapapod.com?subject=Order for ${snap.selectedModel}&body=${encodedBody}`

                  // Open mailto client
                  window.location.href = mailtoLink
                }}
              >
                Email your Pod
                <AiFillMail size="1.3em" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Bottom Panel */}
      {isMobile && (
        <>
          {/* Overlay to block canvas during drag */}
          {isDraggingPanel && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999,
              pointerEvents: 'all',
            }} />
          )}
          <div className="mobile-panel-content" style={{
            maxHeight: `${mobilePanelHeight}px`,
            overflow: isMobilePanelExpanded ? 'auto' : 'hidden',
            transition: dragStateRef.current ? 'none' : 'max-height 0.2s ease',
            pointerEvents: 'auto',
            zIndex: 1000,
          }}>
            {/* Collapse/Expand Handle - Larger draggable area */}
            <div
              onMouseDown={(e) => {
                e.stopPropagation()
                handleMobilePanelDragStart(e)
              }}
              onTouchStart={(e) => {
                e.stopPropagation()
                handleMobilePanelDragStart(e)
              }}
              onClick={(e) => e.stopPropagation()}
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '20px 10px',
                cursor: 'ns-resize',
                borderBottom: isMobilePanelExpanded ? '1px solid #e0e0e0' : 'none',
                marginBottom: isMobilePanelExpanded ? '10px' : '0',
                touchAction: 'none',
                userSelect: 'none',
                pointerEvents: 'auto',
              }}
            >
              <div style={{
                width: '50px',
                height: '5px',
                background: '#999',
                borderRadius: '3px',
              }} />
            </div>

          {isMobilePanelExpanded && (
            <>
              {/* Model Selection */}
              <div className="mobile-section">
                <h3 className="mobile-section-title">
                  Pod Selection
                  <span style={{ fontSize: '0.85rem', color: '#666' }}>{snap.selectedModel}</span>
                </h3>
                <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', padding: '5px 0' }}>
                  {snap.models.map((model) => (
                    <div
                      key={model}
                      onClick={() => {
                        state.selectedModel = model
                        const newModelMaterials = modelMaterials[model] || []
                        if (newModelMaterials.length > 0) {
                          state.selectedMaterial = newModelMaterials[0]
                        }
                        invalidate()
                      }}
                      style={{
                        minWidth: '70px',
                        height: '70px',
                        borderRadius: '8px',
                        border: snap.selectedModel === model ? '3px solid #FFC107' : '2px solid #e0e0e0',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        background: 'white',
                        boxShadow: snap.selectedModel === model ? '0 2px 8px rgba(255, 193, 7, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <img
                        src={model + '_thumb.png'}
                        alt={model}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Material Selection */}
              <div className="mobile-section">
                <h3 className="mobile-section-title">Material Selection</h3>
                <p className="mobile-material-label">{getDisplayName(snap.selectedMaterial)}</p>
                <select
                  value={snap.selectedMaterial}
                  onChange={(e) => (state.selectedMaterial = e.target.value)}
                >
                  {(modelMaterials[snap.selectedModel] || []).map((mat) => (
                    <option key={mat} value={mat}>
                      {getDisplayName(mat)}
                    </option>
                  ))}
                </select>

                {/* Color Swatches */}
                <div className="color-options">
                  {snap.colors.map((color) => (
                    <div
                      key={color.colorHex}
                      className="circle"
                      style={{
                        background: color.colorHex,
                        border: selectedColorName === color.name ? '3px solid #333' : '3px solid #ddd',
                      }}
                      onClick={() => {
                        state.color = color.colorHex
                        setSelectedColorName(color.name)

                        const existingItemIndex = state.pickedItems.findIndex(
                          item => item.material === snap.selectedMaterial && item.model === snap.selectedModel
                        )

                        if (existingItemIndex >= 0) {
                          state.pickedItems[existingItemIndex] = {
                            model: snap.selectedModel,
                            material: snap.selectedMaterial,
                            color: color.name,
                            colorHex: color.colorHex
                          }
                        } else {
                          state.pickedItems.push({
                            model: snap.selectedModel,
                            material: snap.selectedMaterial,
                            color: color.name,
                            colorHex: color.colorHex
                          })
                        }
                        invalidate()
                      }}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>

              {/* Add-ons Section */}
              <div className="mobile-section">
                <h3 className="mobile-section-title">Add-ons</h3>
                <div className="mobile-addons">
                  <button
                    className={snap.showWheels ? 'active' : ''}
                    onClick={() => {
                      state.showWheels = !snap.showWheels
                      updateEnabledAddons({ ...snap, showWheels: !snap.showWheels })
                      invalidate()
                    }}
                  >
                    Show Wheels
                  </button>
                  {(snap.selectedModel === 'QuadPod' || snap.selectedModel === 'DoublePod') && (
                    <button
                      className={snap.showTV ? 'active' : ''}
                      onClick={() => {
                        state.showTV = !snap.showTV
                        updateEnabledAddons({ ...snap, showTV: !snap.showTV })
                        invalidate()
                      }}
                    >
                      Show TV
                    </button>
                  )}
                  <button
                    className={snap.frostedGlass ? 'active' : ''}
                    onClick={() => {
                      state.frostedGlass = !snap.frostedGlass
                      updateEnabledAddons({ ...snap, frostedGlass: !snap.frostedGlass })
                      invalidate()
                    }}
                  >
                    Frosted Glass
                  </button>
                  <button
                    className={snap.showDoor ? 'active' : ''}
                    onClick={() => {
                      state.showDoor = !snap.showDoor
                      updateEnabledAddons({ ...snap, showDoor: !snap.showDoor })
                      invalidate()
                    }}
                  >
                    Show Door
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
                <button
                  style={{
                    flex: 1,
                    padding: '15px',
                    fontSize: '0.95rem',
                    borderRadius: '8px',
                    background: '#FFC107',
                    color: '#333',
                    fontWeight: 700,
                    textAlign: 'center',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                  onClick={() => {
                    generatePDF(snap, modelMaterials, getDisplayName)
                  }}
                >
                  PDF
                  <AiOutlineFilePdf size="1.2em" />
                </button>
                <button
                  style={{
                    flex: 1,
                    padding: '15px',
                    fontSize: '0.95rem',
                    borderRadius: '8px',
                    background: '#FFC107',
                    color: '#333',
                    fontWeight: 700,
                    textAlign: 'center',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                  onClick={() => {
                    const currentModelMaterials = modelMaterials[snap.selectedModel] || []
                    const currentModelPickedItems = snap.pickedItems.filter(item => item.model === snap.selectedModel)
                    const materialList = currentModelMaterials.map((material, index) => {
                      const pickedItem = currentModelPickedItems.find(item => item.material === material)
                      const colorName = pickedItem ? pickedItem.color : 'Default'
                      return `${index + 1}. Material: ${getDisplayName(material)}, Color: ${colorName}`
                    }).join('\n')

                    const addonsList = snap.enabledAddons.length > 0
                      ? `Add-ons: ${snap.enabledAddons.join(', ')}`
                      : 'Add-ons: None selected'

                    const bodyText = `Order Details:
Selected Model: ${snap.selectedModel}

Material Selections:
${materialList}

${addonsList}
`
                    const encodedBody = encodeURIComponent(bodyText)
                    const mailtoLink = `mailto:info@dapapod.com?subject=Order for ${snap.selectedModel}&body=${encodedBody}`
                    window.location.href = mailtoLink
                  }}
                >
                  Email
                  <AiFillMail size="1.2em" />
                </button>
              </div>
            </>
          )}
          </div>
        </>
      )}

      {/* Decals - Desktop only */}
      {!isMobile && (
        <div className="decals">
          <div className="decals--container">
            {snap.models.map((decal) => (
              <div
                key={decal}
                className="decal"
                onClick={() => {
                  state.selectedModel = decal
                  // Auto-select the first material for the new model
                  const newModelMaterials = modelMaterials[decal] || []
                  if (newModelMaterials.length > 0) {
                    state.selectedMaterial = newModelMaterials[0]
                  }
                  invalidate()
                }}
              >
                <img src={decal + '_thumb.png'} alt="brand" />
              </div>
            ))}
          </div>
        </div>
      )}

      <button className="exit" style={{ background: snap.color || '#DCA127', color: getTextColor() }} onClick={() => (state.intro = true)}>
        GO BACK
        <AiOutlineArrowLeft size="1.3em" />
      </button>
    </div>
  )
}


